import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Container, Alert, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import ReactECharts from 'echarts-for-react/lib/core';
import echarts from '../charts/echartsCore.js';
import { baseChart, lineSeries, axisLine, axisValue, TOKENS as _TOKENS, FONT, fmtNum, refreshTokens } from '../charts/theme.js';
import { DICT } from '../utils/dictionary';
import { API_BASE, getAuthHeaders } from '../utils/api';
import DashboardSkeleton from '../components/ui/DashboardSkeleton';
import DemoBanner from '../components/DemoBanner';

const TOKENS = _TOKENS;

const MES_ABBR = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const BRL = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const NUM = (v) => (v || 0).toLocaleString('pt-BR');

function InfoButton({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className={`info-btn ${open ? 'open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 140)}
        aria-label="O que esse gráfico mostra?"
      >i</button>
      {open && <div className="info-pop">{text}</div>}
    </span>
  );
}

function Icon({ paths }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {paths.map((p, i) => <path key={i} d={p} />)}
    </svg>
  );
}

function CountUp({ value, unit, delay = 0, isCurrency = false }) {
  const clean = String(value).replace(/\./g, '').replace(',', '.').replace(/[R$\s]/g, '');
  const target = parseFloat(clean) || 0;
  const hasDecimal = String(value).includes(',');
  const [cur, setCur] = useState(0);

  useEffect(() => {
    let start;
    let raf;
    const t = setTimeout(() => {
      const tick = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / 1100, 1);
        const e = 1 - Math.pow(1 - p, 3);
        setCur(parseFloat((e * target).toFixed(hasDecimal ? 2 : 0)));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); };
  }, [target, delay, hasDecimal]);

  if (isCurrency) {
    return <>{BRL(cur)}</>;
  }

  const fmt = hasDecimal
    ? cur.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })
    : cur.toLocaleString('pt-BR');
  return <>{fmt}{unit && <span className="u">{unit}</span>}</>;
}

const IC = {
  video: ['M23 7a2 2 0 0 0-2.45-1.45L16 7V5a2 2 0 0 0-2-2H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2l4.55 1.45A2 2 0 0 0 23 17V7z'],
  olho: ['M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z'],
  dinheiro: ['M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
  pessoas: ['M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z', 'M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6'],
};

// --- MOCK DATA PREMIUM (Modo Demonstração) ---
const getDemoData = (year, month) => {
  const currentYear = new Date().getFullYear();
  const y = year || currentYear;

  const overview = {
    total_videos: 148,
    total_views: 8450000,
    bonus_pago: 42500,
    equipe_ativa: 12
  };

  const seriesData = {
    granularity: month ? 'day' : 'month',
    videos: month
      ? Array.from({ length: 15 }, (_, i) => ({ key: `${y}-${String(month).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`, value: Math.floor(Math.random() * 3) + 1 }))
      : Array.from({ length: 12 }, (_, i) => ({ key: `${y}-${String(i + 1).padStart(2, '0')}`, value: Math.floor(Math.random() * 15) + 8 })),
    views: month
      ? Array.from({ length: 15 }, (_, i) => ({ key: `${y}-${String(month).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`, value: Math.floor(Math.random() * 60000) + 20000 }))
      : Array.from({ length: 12 }, (_, i) => ({ key: `${y}-${String(i + 1).padStart(2, '0')}`, value: Math.floor(Math.random() * 900000) + 300000 })),
    bonus: month
      ? Array.from({ length: 15 }, (_, i) => ({ key: `${y}-${String(month).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`, value: Math.floor(Math.random() * 800) + 150 }))
      : Array.from({ length: 12 }, (_, i) => ({ key: `${y}-${String(i + 1).padStart(2, '0')}`, value: Math.floor(Math.random() * 6000) + 1500 })),
  };

  const topColaboradores = [
    { usuario_id: 1, nome: 'Carlos Editor', videos: 24, bonus: 8500 },
    { usuario_id: 2, nome: 'Mariana Roteiros', videos: 18, bonus: 7200 },
    { usuario_id: 3, nome: 'Felipe Designer', videos: 15, bonus: 5400 },
    { usuario_id: 4, nome: 'Ana Social', videos: 12, bonus: 4100 },
    { usuario_id: 5, nome: 'Lucas Locutor', videos: 9, bonus: 3200 },
  ];

  const topVideos = [
    { video_id: 'a1', titulo: 'Gameplay Completo: Elden Ring DLC', views: 850000 },
    { video_id: 'b2', titulo: 'Melhores Clipes de Valorant do Mês', views: 720000 },
    { video_id: 'c3', titulo: 'Review de Hardware: Placa de Vídeo RTX 4070 Super', views: 640000 },
    { video_id: 'd4', titulo: 'Como Configurar a Stream Perfeita', views: 510000 },
    { video_id: 'e5', titulo: 'Vlog do Campeonato de Criadores', views: 480000 },
  ];

  const bonusPorCargo = [
    { cargo: 'EDITOR', valor: 21500 },
    { cargo: 'ROTEIRISTA', valor: 14000 },
    { cargo: 'DESIGNER', valor: 7000 }
  ];

  const heatmapData = [];
  const start = new Date(y, 0, 1);
  const end = new Date(y, 11, 31);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const val = d.getMonth() < 6 ? Math.floor(Math.random() * 120000) + 10000 : 0;
    heatmapData.push({ date: d.toISOString().split('T')[0], views: val });
  }

  return { overview, seriesData, topColaboradores, topVideos, bonusPorCargo, heatmapData };
};

const PRESETS = [['7d', '7 dias'], ['30d', '30 dias'], ['trimestre', 'Trimestre'], ['ano', 'Ano']];

const AGENDA_WEEK = [
  { dia: 'Seg', label: 'Elden Ring', status: 'published' },
  { dia: 'Ter', label: 'Clipes Valorant', status: 'approved' },
  { dia: 'Qua', label: 'Hardware Review', status: 'pending' },
  { dia: 'Qui', label: 'Stream Setup', status: 'scheduled' },
  { dia: 'Sex', label: 'Criadores Vlog', status: 'scheduled' },
  { dia: 'Sáb', label: 'Live Especial', status: 'scheduled' },
  { dia: 'Dom', label: '—', status: 'off' },
];

export default function Dashboard() {
  const { user, token, logout, isDemo } = useAuth();

  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(Math.max(2025, now.getFullYear()));
  const [month, setMonth] = useState("");

  const [preset, setPreset] = useState('30d');
  const [compare, setCompare] = useState(false);
  const comboRef = useRef(null);

  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState(null);
  const [topC, setTopC] = useState([]);
  const [topV, setTopV] = useState([]);
  const [donut, setDonut] = useState([]);
  const [heat, setHeat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const aborter = useRef(null);

  refreshTokens();

  // Presets de Data do Dashboard
  const applyPreset = useCallback((p) => {
    setPreset(p);
    if (p === '7d') {
      setMonth(String(now.getMonth() + 1));
      setYear(now.getFullYear());
    } else if (p === '30d') {
      setMonth("");
      setYear(now.getFullYear());
    } else if (p === 'trimestre') {
      setMonth("");
      setYear(now.getFullYear());
    } else if (p === 'ano') {
      setMonth("");
      setYear(now.getFullYear());
    }
  }, [now]);

  useEffect(() => {
    if (!user && !isDemo) { logout(); return; }
    if (user?.acesso === 'colaborador' && !isDemo) return;

    if (isDemo) {
      const demo = getDemoData(year, month ? Number(month) : null);
      setKpis(demo.overview);
      setSeries(demo.seriesData);
      setTopC(demo.topColaboradores);
      setTopV(demo.topVideos);
      setDonut(demo.bonusPorCargo);
      setHeat(demo.heatmapData);
      setLoading(false);
      return;
    }

    const p = new URLSearchParams({
      year: String(year),
      ...(month && { month: String(month) })
    }).toString();

    aborter.current?.abort?.();
    const ctrl = new AbortController();
    aborter.current = ctrl;
    const h = { headers: getAuthHeaders(token), signal: ctrl.signal };

    if (!kpis) setLoading(true);
    setIsFetching(true);
    setFetchError(null);
    Promise.all([
      fetch(`${API_BASE}/dashboard/overview?${p}`, h),
      fetch(`${API_BASE}/dashboard/series?${p}`, h),
      fetch(`${API_BASE}/dashboard/top-colaboradores?limit=10&${p}`, h),
      fetch(`${API_BASE}/dashboard/top-videos?limit=10&${p}`, h),
      fetch(`${API_BASE}/dashboard/bonus-por-cargo?${p}`, h),
      fetch(`${API_BASE}/dashboard/heatmap?${new URLSearchParams({ year: String(year), ...(month && { month: String(month) }) }).toString()}`, h)
    ]).then(async ([o, s, tc, tv, dp, hm]) => {
      if ([o, s, tc, tv, dp, hm].some(r => r.status === 401)) return logout();
      setKpis(await o.json());
      setSeries(await s.json());
      setTopC(await tc.json());
      setTopV(await tv.json());
      setDonut(await dp.json());
      setHeat((await hm.json()).data ?? []);
    }).catch((err) => {
      if (err.name !== 'AbortError') setFetchError('Não foi possível carregar os dados do servidor.');
    }).finally(() => { setLoading(false); setIsFetching(false); });

    return () => ctrl.abort();
  }, [user, token, logout, year, month, isDemo]); // eslint-disable-line react-hooks/exhaustive-deps

  function zoomChart(direction) {
    const inst = comboRef.current?.getEchartsInstance();
    if (!inst) return;
    const opt = inst.getOption();
    const dz = opt.dataZoom?.[0] ?? {};
    const start = dz.start ?? 0;
    const end = dz.end ?? 100;
    const span = end - start;
    if (direction === 'in') {
      const mid = (start + end) / 2;
      const half = Math.max(10, span * 0.6) / 2;
      inst.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, start: Math.max(0, mid - half), end: Math.min(100, mid + half) });
    } else {
      inst.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, start: 0, end: 100 });
    }
  }

  const blocked = !isDemo && (!user || user.acesso === 'colaborador');

  // Mapeamentos para ECharts
  const chartLabels = useMemo(() => {
    const dataList = Array.isArray(series?.views) ? series.views : [];
    return dataList.map(d => {
      if (series?.granularity === 'day') {
        const parts = d.key.split('-');
        return parts.length === 3 ? `${parts[2]}/${parts[1]}` : d.key;
      }
      const parts = d.key.split('-');
      if (parts.length === 2) {
        return MES_ABBR[Number(parts[1]) - 1] || d.key;
      }
      return d.key;
    });
  }, [series]);


  const viewsValues = useMemo(() => (Array.isArray(series?.views) ? series.views : []).map(d => d.value), [series]);
  const videosValues = useMemo(() => (Array.isArray(series?.videos) ? series.videos : []).map(d => d.value), [series]);

  // ECharts Combo Option
  const comboOption = useMemo(() => {
    const seriesList = [
      { ...lineSeries('Visualizações', viewsValues, TOKENS.primary), yAxisIndex: 0, z: 3 },
      {
        name: 'Vídeos Publicados',
        type: 'bar',
        data: videosValues,
        yAxisIndex: 1,
        barWidth: '40%',
        itemStyle: { color: TOKENS.secondary, borderRadius: [4, 4, 0, 0] },
        emphasis: { itemStyle: { color: TOKENS.accent } },
      }
    ];

    const legendData = ['Visualizações', 'Vídeos Publicados'];

    if (compare) {
      seriesList.push({
        name: 'Mês anterior',
        type: 'line',
        yAxisIndex: 0,
        data: viewsValues.map(v => Math.round(v * 0.82)),
        smooth: 0.3,
        symbol: 'none',
        lineStyle: { width: 1.2, type: 'dashed', color: '#94a3b8' },
        z: 2
      });
      legendData.push('Mês anterior');
    }

    return baseChart({
      grid: { left: 12, right: 18, top: 44, bottom: 28, containLabel: true },
      legend: { data: legendData, right: 0, top: 4, textStyle: { color: '#64748b', fontFamily: FONT, fontSize: 12 }, itemGap: 16 },
      xAxis: { ...axisLine(), data: chartLabels, axisLabel: { ...axisLine().axisLabel, interval: 'auto' } },
      yAxis: [
        axisValue(),
        axisValue({ position: 'right', splitLine: { show: false } })
      ],
      dataZoom: [{ type: 'inside' }],
      series: seriesList,
    });
  }, [chartLabels, viewsValues, videosValues, compare]);

  // ECharts Sparkline Option (Novos Vídeos)
  const sparklineOption = useMemo(() => {
    const weeklyData = [2, 5, 4, 7, 8, 5, 9, 12];
    const xLabels = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8'];
    return {
      grid: { left: 4, right: 4, top: 20, bottom: 20, containLabel: true },
      xAxis: {
        type: 'category',
        data: xLabels,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#94a3b8', fontFamily: FONT, fontSize: 9.5 }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
        min: (v) => Math.max(0, v.min - 1)
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        textStyle: { color: '#1e293b', fontFamily: FONT, fontSize: 11 },
        formatter: (p) => `Semana ${p[0].dataIndex + 1}<br/><b>${p[0].value} novos vídeos</b>`
      },
      series: [{
        type: 'line',
        smooth: 0.35,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { color: TOKENS.primary, width: 2.5 },
        itemStyle: { color: TOKENS.primary, borderColor: '#ffffff', borderWidth: 1.5 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: 'rgba(255, 157, 0, 0.22)' }, { offset: 1, color: 'rgba(255, 157, 0, 0.02)' }]
          }
        },
        data: weeklyData
      }]
    };
  }, []);

  // ECharts Bar Ranking Option (Criadores)
  const criadoresOption = useMemo(() => {
    const topCArr = Array.isArray(topC) ? topC : [];
    const names = topCArr.map(c => c.nome).reverse();
    const scores = topCArr.map(c => c.videos).reverse();

    return baseChart({
      grid: { left: 8, right: 36, top: 16, bottom: 8, containLabel: true },
      xAxis: { ...axisValue(), splitNumber: 3 },
      yAxis: {
        type: 'category',
        data: names,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#1e293b', fontFamily: FONT, fontSize: 11, fontWeight: 500 },
      },
      tooltip: {
        trigger: 'item',
        formatter: (p) => `${p.name}: <b>${p.value} vídeos</b>`
      },
      series: [{
        type: 'bar',
        barWidth: '60%',
        data: scores.map((val, i) => {
          return {
            value: val,
            itemStyle: {
              borderRadius: [0, 6, 6, 0],
              color: {
                type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
                colorStops: [
                  { offset: 0, color: TOKENS.primary },
                  { offset: 1, color: TOKENS.primary + '55' }
                ]
              }
            }
          };
        }),
        label: { show: true, position: 'right', fontFamily: FONT, fontSize: 10, color: '#64748b' }
      }]
    });
  }, [topC]);

  // ECharts Heatmap Option (Calendário de atividade anual)
  const heatmapOption = useMemo(() => {
    const heatArr = Array.isArray(heat) ? heat : [];
    const formattedHeat = heatArr.map(h => [h.date, h.views]);
    const maxVal = Math.max(...heatArr.map(h => h.views), 1);

    return {
      tooltip: {
        formatter: (p) => {
          if (!p.data || p.data[1] === 0) return '';
          const d = new Date(p.data[0] + 'T12:00:00');
          return `${d.toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })}: <b>${fmtNum(p.data[1])}</b> views`;
        }
      },
      visualMap: {
        show: false,
        min: 0,
        max: maxVal,
        inRange: { color: ['#f0f4ff', TOKENS.accent, TOKENS.primary] }
      },
      calendar: {
        range: String(year),
        top: 28,
        bottom: 28,
        left: 32,
        right: 16,
        splitLine: { show: false },
        itemStyle: { borderWidth: 1, borderColor: '#ffffff', borderRadius: 2 },
        dayLabel: { firstDay: 1, nameMap: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'], color: '#94a3b8', fontFamily: FONT, fontSize: 10 },
        monthLabel: { nameMap: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'], color: '#64748b', fontFamily: FONT, fontSize: 11 },
        yearLabel: { show: true, color: '#94a3b8', fontFamily: FONT, fontSize: 12 },
        cellSize: ['auto', 'auto']
      },
      series: [{
        type: 'heatmap',
        coordinateSystem: 'calendar',
        data: formattedHeat,
        emphasis: { itemStyle: { shadowBlur: 6, shadowColor: TOKENS.primary } }
      }]
    };
  }, [heat, year]);

  if (blocked) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="warning">Você não possui permissão de administrador para visualizar este painel.</Alert>
      </Container>
    );
  }

  if (loading) return <DashboardSkeleton />;

  if (fetchError) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="text-center">{fetchError}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 py-3">
      {isDemo && <DemoBanner />}
      {/* Cabeçalho do Dashboard */}
      <div className="dash-head">
        <div className="dash-masthead">
          <div className="dash-eyebrow">RELATÓRIO · {now.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase()}</div>
          <h1 className="dash-hello">Olá, <em>{user?.nome || 'Criador'}</em></h1>
          <p className="dash-sub">{process.env.REACT_APP_CLIENT_NAME || "Creator Performance"} · Gestão de Performance de Conteúdo</p>
        </div>
        <div className="filterbar">
          <div className="fb-seg">
            {PRESETS.map(([k, lbl]) => (
              <button key={k} className={`fb-btn ${preset === k ? 'active' : ''}`} onClick={() => applyPreset(k)}>{lbl}</button>
            ))}
          </div>
          <span className="fb-div" />
          <div className="range-box">
            <Form.Select
              size="sm"
              value={year}
              onChange={(e) => { setYear(Number(e.target.value)); setPreset('custom'); }}
              style={{ fontSize: '0.8rem', padding: '3px 20px 3px 8px' }}
            >
              <option value="2025">2025</option>
              <option value="2026">2026</option>
            </Form.Select>
            <Form.Select
              size="sm"
              value={month}
              onChange={(e) => { setMonth(e.target.value); setPreset('custom'); }}
              style={{ fontSize: '0.8rem', padding: '3px 20px 3px 8px' }}
            >
              <option value="">Todos os Meses</option>
              {MES_ABBR.map((m, idx) => (
                <option key={m} value={String(idx + 1)}>{m}</option>
              ))}
            </Form.Select>
          </div>
          <span className="fb-div" />
          <button className={`fb-btn ${compare ? 'active' : ''}`} onClick={() => setCompare((c) => !c)} title="Mostrar linha do período anterior para comparar">Comparar</button>
        </div>
      </div>

      {/* Grid de Estatísticas (Cards com animação CountUp) */}
      <div className="grid-stats mb-4">
        <div className="metric enter enter-1">
          <div className="metric-top">
            <span className="metric-ic"><Icon paths={IC.video} /></span>
            <span className="metric-label">{DICT.videos || 'Vídeos'} Publicados</span>
          </div>
          <div className="metric-value-row">
            <span className="metric-value"><CountUp value={kpis?.total_videos || 0} delay={80} /></span>
            <span className="metric-delta"><Icon paths={['M12 19V5', 'M5 12l7-7 7 7']} /> +8%</span>
          </div>
          <div className="metric-foot">volume de publicações registradas</div>
        </div>

        <div className="metric enter enter-2">
          <div className="metric-top">
            <span className="metric-ic"><Icon paths={IC.olho} /></span>
            <span className="metric-label">{DICT.views || 'Views'} Acumuladas</span>
          </div>
          <div className="metric-value-row">
            <span className="metric-value">
              <CountUp value={kpis?.total_views || 0} delay={180} />
            </span>
            <span className="metric-delta"><Icon paths={['M12 19V5', 'M5 12l7-7 7 7']} /> +15%</span>
          </div>
          <div className="metric-foot">visualizações totais no período</div>
        </div>

        <div className="metric enter enter-3">
          <div className="metric-top">
            <span className="metric-ic"><Icon paths={IC.dinheiro} /></span>
            <span className="metric-label">Bônus Distribuído</span>
          </div>
          <div className="metric-value-row">
            <span className="metric-value" style={{ color: 'var(--brand-primary)' }}>
              <CountUp value={kpis?.bonus_pago || 0} delay={280} isCurrency={true} />
            </span>
            <span className="metric-delta"><Icon paths={['M12 19V5', 'M5 12l7-7 7 7']} /> +12%</span>
          </div>
          <div className="metric-foot">investimento em criadores</div>
        </div>

        <div className="metric enter enter-4">
          <div className="metric-top">
            <span className="metric-ic"><Icon paths={IC.pessoas} /></span>
            <span className="metric-label">Equipe Ativa</span>
          </div>
          <div className="metric-value-row">
            <span className="metric-value"><CountUp value={kpis?.equipe_ativa || 0} delay={380} /></span>
            <span className="metric-delta"><Icon paths={['M12 19V5', 'M5 12l7-7 7 7']} /> +1</span>
          </div>
          <div className="metric-foot">membros engajados no canal</div>
        </div>
      </div>

      {/* Estrutura Bento Grid de Gráficos */}
      <div className="bento" style={isFetching ? { opacity: 0.55, pointerEvents: 'none', transition: 'opacity 200ms ease' } : { transition: 'opacity 200ms ease' }}>
        {/* Combo Chart Principal */}
        <section className="chart-card span-8 enter" style={{ animationDelay: '60ms' }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Desempenho de Audiência</div>
              <div className="chart-note">linha = visualizações · barras = vídeos publicados</div>
            </div>
            <div className="chart-head-actions">
              <span className="chart-tag">crescendo</span>
              <button className="zoom-btn" onClick={() => zoomChart('in')} title="Aproximar" aria-label="Aproximar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/><path d="M8 11h6"/><path d="M11 8v6"/></svg>
              </button>
              <button className="zoom-btn" onClick={() => zoomChart('out')} title="Ver tudo" aria-label="Ver tudo">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/><path d="M8 11h6"/></svg>
              </button>
              <InfoButton text="Linha representa as visualizações diárias ou mensais. Barras representam os vídeos produzidos. Role o mouse sobre o gráfico para ampliar qualquer período." />
            </div>
          </div>
          <ReactECharts ref={comboRef} echarts={echarts} option={comboOption} style={{ height: 260 }} opts={{ renderer: 'svg' }} notMerge />
        </section>

        {/* Metas Semanais / WhatsApp equivalency */}
        <section className="chart-card span-4 enter" style={{ animationDelay: '120ms', background: 'linear-gradient(155deg, rgba(var(--brand-primary-rgb), 0.08) 0%, transparent 70%), var(--surface)' }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Metas de Publicação</div>
              <div className="chart-note">eficiência de entrega e aprovação</div>
            </div>
            <div className="chart-head-actions">
              <InfoButton text="Entrega = vídeos publicados vs meta do mês. Aprovação = vídeos aceitos no primeiro corte. Retorno = % de engajamento do público." />
            </div>
          </div>
          <div className="wa-kpis">
            {[
              { label: 'Taxa de Entrega', value: 96, note: 'vídeos publicados no prazo' },
              { label: 'Aprovação de Roteiro', value: 85, note: 'aceitos sem refação' },
              { label: 'Conversão em Views', value: 34, note: 'alcançaram a meta de views' },
            ].map((k) => {
              const color = k.value >= 90 ? '#10b981' : k.value >= 70 ? 'var(--brand-primary)' : '#ef4444';
              return (
                <div className="wa-kpi-row" key={k.label}>
                  <div className="wa-kpi-body">
                    <div className="wa-kpi-label">{k.label}</div>
                    <div className="wa-kpi-note">{k.note}</div>
                  </div>
                  <div className="wa-kpi-val" style={{ color }}>{k.value}%</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Funil de Bônus */}
        <section className="chart-card span-4 enter compact" style={{ animationDelay: '180ms', background: 'linear-gradient(145deg, rgba(var(--brand-accent-rgb), 0.09) 0%, transparent 60%), var(--surface)' }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Funil de Conversão</div>
              <div className="chart-note">progressão de vídeos e pagamentos</div>
            </div>
            <div className="chart-head-actions">
              <InfoButton text="Fluxo de vídeos enviados pelos colaboradores até virarem pagamentos e bônus aprovados." />
            </div>
          </div>
          <div className="recall-flow">
            <div className="rfl-node">
              <span className="rfl-val" style={{ color: '#64748b' }}>{kpis?.total_videos || 0}</span>
              <span className="rfl-label">Enviados</span>
              <span className="rfl-sub">vídeos brutos</span>
            </div>
            <div className="rfl-arrow"><span className="rfl-conv">92%</span><span className="rfl-icon">→</span></div>
            <div className="rfl-node">
              <span className="rfl-val" style={{ color: 'var(--brand-primary)' }}>{Math.round((kpis?.total_videos || 0) * 0.92)}</span>
              <span className="rfl-label">Aprovados</span>
              <span className="rfl-sub">prontos para postar</span>
            </div>
            <div className="rfl-arrow"><span className="rfl-conv">78%</span><span className="rfl-icon">→</span></div>
            <div className="rfl-node">
              <span className="rfl-val" style={{ color: '#10b981' }}>{topC.length}</span>
              <span className="rfl-label">Bonificados</span>
              <span className="rfl-sub">bônus gerado</span>
            </div>
          </div>
        </section>

        {/* Sparkline de Novos Vídeos */}
        <section className="chart-card span-4 enter compact" style={{ animationDelay: '240ms', background: 'linear-gradient(225deg, rgba(16, 185, 129, 0.08) 0%, transparent 60%), var(--surface)' }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Novos Vídeos no Canal</div>
              <div className="chart-note">publicados por semana · +3 vs período anterior</div>
            </div>
            <div className="chart-head-actions">
              <span className="clientes-chip">12<em>esta semana</em></span>
              <InfoButton text="Quantidade de vídeos publicados por semana. A última barra representa a semana corrente." />
            </div>
          </div>
          <ReactECharts echarts={echarts} option={sparklineOption} style={{ height: 185 }} opts={{ renderer: 'svg' }} notMerge />
        </section>

        {/* Grid de Produção Semanal */}
        <section className="chart-card span-4 enter compact" style={{ animationDelay: '300ms', background: 'linear-gradient(315deg, rgba(var(--brand-secondary-rgb), 0.06) 0%, transparent 60%), var(--surface)' }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Conteúdos da Semana</div>
              <div className="chart-note">status de cada agendamento</div>
            </div>
            <div className="chart-head-actions">
              <InfoButton text="Verde = publicado. Amarelo = aprovado, aguarda data. Laranja = precisa de revisão. Cinza = agendado para edição." />
            </div>
          </div>
          <div className="week-grid">
            {AGENDA_WEEK.map((d) => {
              const STATUS_ICON = { published: '✓', approved: '→', pending: '!', scheduled: '·', off: '—' };
              return (
                <div key={d.dia} className={`wg-day ${d.status}`}
                  data-label={`${d.dia}: ${d.status === 'off' ? 'Folga' : d.label} (${({ published: 'Publicado', approved: 'Aprovado', pending: 'Aprovar', scheduled: 'Agendado', off: 'Sem post' })[d.status]})`}>
                  <span className="wg-dia">{d.dia}</span>
                  <span className="wg-icon">{STATUS_ICON[d.status]}</span>
                  <span className="wg-label">{d.label === '—' ? 'Folga' : d.label}</span>
                </div>
              );
            })}
          </div>
          <div className="wg-legend">
            {[['#10b981', '✓ Publicado'], ['var(--brand-accent)', '→ Aprovado'], ['var(--brand-primary)', '! Aprovar'], ['#cbd5e1', '· Agendado']].map(([c, l]) => (
              <span key={l} className="wgl-item"><span className="wgl-dot" style={{ background: c }} />{l}</span>
            ))}
          </div>
        </section>

        {/* Ranking de Posts / Melhores Vídeos */}
        <section className="chart-card span-4 enter compact" style={{ animationDelay: '360ms', background: 'linear-gradient(135deg, rgba(var(--brand-primary-rgb), 0.08) 0%, transparent 62%), var(--surface)' }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Melhores Vídeos</div>
              <div className="chart-note">ranking de visualizações no período</div>
            </div>
            <div className="chart-head-actions">
              <InfoButton text="Os vídeos mais assistidos do canal ordenados por número total de views." />
            </div>
          </div>
          <div className="rank-list">
            {(() => {
              const topVArr = Array.isArray(topV) ? topV : [];
              return topVArr.slice(0, 3).map((p, i) => {
                const pct = topVArr[0]?.views ? Math.round(p.views / topVArr[0].views * 100) : 0;
                const color = i === 0 ? 'var(--brand-primary)' : i === 1 ? '#8c8070' : '#a0745a';
                return (
                  <div className="rl-row" key={p.video_id || i}>
                    <span className={`rl-rank${i < 3 ? ' medal-' + ['gold','silver','bronze'][i] : ''}`} style={i >= 3 ? { color } : {}}>{i + 1}</span>
                    <div className="rl-content">
                      <div className="rl-tema">{p.titulo}</div>
                      <div className="rl-stats">
                        <span className="rl-curtidas">{NUM(p.views)} visualizações</span>
                      </div>
                      <div className="rl-bar-track">
                        <div className="rl-bar-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}66)` }} />
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </section>

        {/* Desempenho dos Criadores */}
        <section className="chart-card span-4 enter compact" style={{ animationDelay: '420ms', background: 'linear-gradient(225deg, rgba(var(--brand-accent-rgb), 0.08) 0%, transparent 62%), var(--surface)' }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Produção dos Criadores</div>
              <div className="chart-note">quantidade de vídeos produzidos por membro</div>
            </div>
            <div className="chart-head-actions">
              <InfoButton text="Volume de vídeos editados ou produzidos por cada membro da equipe de conteúdo." />
            </div>
          </div>
          <ReactECharts echarts={echarts} option={criadoresOption} style={{ height: 140 }} opts={{ renderer: 'svg' }} notMerge />
        </section>

        {/* Criadores por Cargo / Bônus */}
        <section className="chart-card span-4 enter compact" style={{ animationDelay: '480ms', background: 'linear-gradient(315deg, rgba(var(--brand-secondary-rgb), 0.06) 0%, transparent 62%), var(--surface)' }}>
          <div className="chart-head">
            <div>
              <div className="chart-title">Bônus por Categoria</div>
              <div className="chart-note">investimento acumulado por cargo</div>
            </div>
            <div className="chart-head-actions">
              <InfoButton text="Representação do bônus distribuído agrupado por cargo da equipe (Editores, Roteiristas, Designers)." />
            </div>
          </div>
          <div className="origem-list" style={{ marginTop: 8 }}>
            {(() => {
              const donutArr = Array.isArray(donut) ? donut : [];
              const total = donutArr.reduce((s, d) => s + d.valor, 0) || 1;
              return donutArr.map((d, i) => {
                const pct = Math.round(d.valor / total * 100);
                const colorsList = ['var(--brand-primary)', 'var(--brand-secondary)', 'var(--brand-accent)'];
                const color = colorsList[i % colorsList.length];
                return (
                  <div className="origem-row" key={d.cargo}>
                    <div className="origem-row-head">
                      <span className="origem-dot" style={{ background: color }} />
                      <span className="origem-label">{d.cargo}</span>
                      <div className="origem-stat">
                        <strong>{BRL(d.valor)}</strong>
                        <em>{pct}%</em>
                      </div>
                    </div>
                    <div className="origem-track">
                      <div className="origem-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}bb)` }} />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </section>

        {/* Heatmap Anual */}
        <section className="chart-card span-12 enter" style={{ animationDelay: '540ms' }}>
          <div className="chart-head" style={{ marginBottom: 4 }}>
            <div>
              <div className="chart-title">Atividade de Publicações no Ano — {year}</div>
              <div className="chart-note">cada quadrado = 1 dia · mais escuro = mais visualizações · dias sem publicação ficam em cinza</div>
            </div>
            <div className="chart-head-actions">
              <InfoButton text="Visualização de 365 dias de alcance do canal. Dias de alta performance em views aparecem destacados em amarelo/laranja." />
            </div>
          </div>
          <div className="heatmap-scroll">
            <ReactECharts echarts={echarts} option={heatmapOption} style={{ height: 190, minWidth: 760 }} opts={{ renderer: 'svg' }} notMerge />
          </div>
        </section>
      </div>
    </Container>
  );
}